// Products over 250 variants only ship variants adjacent to the selection (see snippets/product-variant-json.liquid).
// The full list is loaded in the background from templates/product.variants-json.liquid; until it arrives,
// the list is refreshed after every selection and fetched on demand when a combination is missing.
(() => {
  if (typeof VariantSelects === 'undefined' || typeof subscribe !== 'function') return;

  const JSON_SELECTOR = 'script[type="application/json"]';
  const VARIANTS_PER_PAGE = 250;
  const sectionIdOf = (picker) => picker.dataset.originalSection || picker.dataset.section;

  const setVariantData = (picker, jsonText) => {
    const currentJson = picker.querySelector(JSON_SELECTOR);
    if (currentJson) currentJson.textContent = jsonText;
    picker.variantData = JSON.parse(jsonText);
  };

  const applyVariantJson = (picker, html) => {
    const freshJson = html.querySelector(`#variant-selects-${sectionIdOf(picker)} ${JSON_SELECTOR}`);
    if (!freshJson) return false;
    setVariantData(picker, freshJson.textContent);
    return true;
  };

  const loadAllVariants = (picker) => {
    const pageCount = Math.ceil(Number(picker.dataset.variantsCount) / VARIANTS_PER_PAGE);
    if (!pageCount) return;

    const pages = Array.from({ length: pageCount }, (_, index) =>
      fetch(`${picker.dataset.url}?view=variants-json&page=${index + 1}`).then((response) => {
        if (!response.ok) throw new Error(`Variant page ${index + 1}: ${response.status}`);
        return response.json();
      })
    );

    Promise.all(pages)
      .then((results) => {
        setVariantData(picker, JSON.stringify(results.flatMap((result) => result.variants)));
        picker.hasAllVariants = true;

        if (picker.pendingOptionValues) {
          picker.pendingOptionValues = null;
          picker.onVariantChange();
        } else {
          picker.updateOptions();
          picker.updateVariantStatuses();
        }
      })
      .catch(() => {});
  };

  const selectedOptionValueIds = (picker) =>
    [...picker.querySelectorAll('.product-form__input')].map((field) => {
      const select = field.querySelector('select');
      const chosen = select ? select.selectedOptions[0] : field.querySelector('input:checked');
      return chosen && chosen.dataset.optionValueId;
    });

  // While a combination is being fetched, stop the theme from jumping to some other "available" variant.
  const originalFindAvailableVariant = window.findAvailableVariant;
  if (typeof originalFindAvailableVariant === 'function') {
    window.findAvailableVariant = function (picker, ...rest) {
      if (picker && picker.pendingOptionValues) return true;
      return originalFindAvailableVariant.call(this, picker, ...rest);
    };
  }

  const originalUpdateMasterId = VariantSelects.prototype.updateMasterId;
  VariantSelects.prototype.updateMasterId = function () {
    originalUpdateMasterId.call(this);
    this.pendingOptionValues = null;
    if (this.currentVariant || !this.dataset.largeProduct || this.hasAllVariants) return;

    const ids = selectedOptionValueIds(this);
    if (ids.some((id) => !id)) return;
    const key = ids.join(',');
    // A combination that is still missing after fetching it does not exist; don't refetch it forever.
    if (this.lastFetchedOptionValues === key) return;
    this.lastFetchedOptionValues = key;
    this.pendingOptionValues = key;

    fetch(`${this.dataset.url}?option_values=${key}&section_id=${sectionIdOf(this)}`)
      .then((response) => response.text())
      .then((text) => {
        if (this.pendingOptionValues !== key) return;
        this.pendingOptionValues = null;
        const html = new DOMParser().parseFromString(text, 'text/html');
        if (applyVariantJson(this, html)) this.onVariantChange();
      })
      .catch(() => {
        if (this.pendingOptionValues !== key) return;
        this.pendingOptionValues = null;
        this.lastFetchedOptionValues = null;
        this.onVariantChange();
      });
  };

  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    if (!data || !data.html) return;

    document.querySelectorAll('variant-selects[data-large-product]').forEach((picker) => {
      if (picker.hasAllVariants || sectionIdOf(picker) !== data.sectionId) return;
      if (!applyVariantJson(picker, data.html)) return;

      picker.lastFetchedOptionValues = null;
      picker.updateOptions();
      picker.updateVariantStatuses();
    });
  });

  const LARGE_PICKER_SELECTOR = 'variant-selects[data-large-product]';
  const initPicker = (picker) => {
    if (picker.largeVariantsInitialized) return;
    picker.largeVariantsInitialized = true;
    loadAllVariants(picker);
  };

  document.querySelectorAll(LARGE_PICKER_SELECTOR).forEach(initPicker);

  // Pickers can also arrive later, e.g. inside the quick-add modal on collection pages.
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches(LARGE_PICKER_SELECTOR)) initPicker(node);
        node.querySelectorAll(LARGE_PICKER_SELECTOR).forEach(initPicker);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
